import { ERentioFormField } from "@rentiohq/shared-frontend/dist/components/components/Form";
import { IUiSchema } from "@rentiohq/shared-frontend/dist/forms/forms.types";
import { getValue } from "@rentiohq/shared-frontend/dist/redux/form/form.utils";
import {
  EPropertyEPCLabel,
  IProperty,
} from "@rentiohq/shared-frontend/dist/types/property.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { formatCurrency } from "@rentiohq/shared-frontend/dist/utils/number.utils";
import { getAvailableEpcLabelsForProperty } from "@rentiohq/shared-frontend/dist/utils/property.utils";
import {
  getMaxFullScoreHousehold,
  getPerfectIncomeForRent,
  MIN_PERFECT_INCOME_FACTOR,
} from "@rentiohq/shared-frontend/dist/utils/rentmatch.utils";
import { JSONSchema6 } from "json-schema";
import { FormValidation } from "react-native-jsonschema-form-test";
import { validationErrorsForMediaUrl } from "utils/mediaUrl.utils";
import { ts } from "../../services";
import { EField, IExtraData } from "./schema.editPropertyInfo.types";

// JSON Schema field augmented with the custom `options` array consumed by the
// SegmentedControl renderer — `options` is not part of the base JSONSchema6.
type ISchemaFieldWithOptions = JSONSchema6 & {
  options: { id: boolean; value: string }[];
};

/**
 * Computes a concrete listing URL example for the placeholder text,
 * using the same substitution logic as rentmatchPreference.repo.ts:
 *   [ID]   → property CRM reference (only shown when a ref is available)
 *   [city] → property city (lowercase)
 *   [ZIP]  → property zip
 *
 * Priority: resolved template URL > websiteUrl > generic i18n placeholder
 * When no CRM ref is known the template is NOT shown (would confuse the broker
 * by displaying a literal "[ID]" in the placeholder).
 */
function computeListingUrlPlaceholder(
  listingUrlTemplate: string | null | undefined,
  websiteUrl: string | null | undefined,
  property: IProperty,
): string {
  if (listingUrlTemplate) {
    // Same COALESCE order as the backend (rentmatchPreference.repo.ts):
    // refWhise → refOmnicasa → refZabun → refDewaele → refSkarabee →
    // refSweepbright → refMaxImmo → refRealSuite
    const crmRef =
      property.refWhise ??
      property.refOmnicasa ??
      property.refZabun ??
      property.refDewaele ??
      property.refSkarabee ??
      property.refSweepbright ??
      property.refMaxImmo ??
      property.refRealSuite ??
      undefined;

    if (crmRef) {
      return listingUrlTemplate
        .replace(/\[ID\]/g, crmRef)
        .replace(/\[city\]/g, (property.city || "").toLowerCase())
        .replace(/\[CITY\]/g, (property.city || "").toLowerCase())
        .replace(/\[ZIP\]/g, property.zip || "");
    }
    // No CRM ref → fall through to websiteUrl or generic placeholder
  }
  if (websiteUrl) {
    return websiteUrl;
  }
  return getLocalizedText(
    "property_info.form.external_listing_url.placeholder",
  );
}

export const getSchema =
  (initialValues: { [key: string]: any }, extraData?: IExtraData) =>
  (defaultValues: { [key: string]: any }): JSONSchema6 => ({
    required: [],
    properties: {
      [EField.Price]: {
        type: ["number", "null"],
        default: getValue(EField.Price, initialValues, defaultValues),
      },
      [EField.Costs]: {
        type: ["number", "null"],
        default: getValue(EField.Costs, initialValues, defaultValues),
      },
      ...(extraData?.hasRentMatchForBroker && {
        [EField.RentMatchIntro]: {
          default: undefined,
        },
        [EField.RoomCountBedroom]: {
          type: ["number", "null"],
          default: getValue(
            EField.RoomCountBedroom,
            initialValues,
            defaultValues,
          ),
        },
        [EField.AvailableAtDate]: {
          default: getValue(
            EField.AvailableAtDate,
            initialValues,
            defaultValues,
          ),
        },
        [EField.IsRentMatchPublished]: {
          type: ["boolean", "null"],
          default: getValue(
            EField.IsRentMatchPublished,
            initialValues,
            defaultValues,
          ),
          options: [
            {
              id: false,
              value: getLocalizedText(
                "property_info.form.is_rent_match_published.option_do_not_publish",
              ),
            },
            {
              id: true,
              value: getLocalizedText(
                "property_info.form.is_rent_match_published.option_publish",
              ),
            },
          ],
        } as ISchemaFieldWithOptions,
        [EField.ExternalListingUrl]: {
          type: ["string", "null"],
          default: getValue(
            EField.ExternalListingUrl,
            initialValues,
            defaultValues,
          ),
        },
        [EField.RoomCountBathroom]: {
          type: ["number", "null"],
          default: getValue(
            EField.RoomCountBathroom,
            initialValues,
            defaultValues,
          ),
        },
        [EField.RoomCountGarage]: {
          type: ["number", "null"],
          default: getValue(
            EField.RoomCountGarage,
            initialValues,
            defaultValues,
          ),
        },
        [EField.LivingSurfaceArea]: {
          type: ["number", "null"],
          default: getValue(
            EField.LivingSurfaceArea,
            initialValues,
            defaultValues,
          ),
        },
        [EField.EpcLabel]: {
          default: getValue(EField.EpcLabel, initialValues, defaultValues),
          // @ts-ignore — options is a custom extension used by InlineSelect
          options: getAvailableEpcLabelsForProperty({}),
        },
        [EField.BuildingTerraceSurfaceArea]: {
          type: ["number", "null"],
          default: getValue(
            EField.BuildingTerraceSurfaceArea,
            initialValues,
            defaultValues,
          ),
        },
        [EField.BuildingGardenSurfaceArea]: {
          type: ["number", "null"],
          default: getValue(
            EField.BuildingGardenSurfaceArea,
            initialValues,
            defaultValues,
          ),
        },
        [EField.DescriptionLong]: {
          type: ["string", "null"],
          default: getValue(
            EField.DescriptionLong,
            initialValues,
            defaultValues,
          ),
        },
        [EField.PublishHeader]: {
          default: undefined,
        },
        [EField.BasicsHeader]: {
          default: undefined,
        },
        [EField.MediaHeader]: {
          default: undefined,
        },
        [EField.ExtraDetailsHeader]: {
          default: undefined,
        },
        // Holds no value: the gallery stores every photo the moment it is
        // added. It is here so the grid can place it.
        [EField.Photos]: {
          default: undefined,
        },
        // No "type" here, deliberately. This form's schema library reads
        // ["object", "null"] as an object and then rebuilds the default from
        // the schema's own properties, of which this field has none, so a
        // declared type silently replaces the stored value with an empty
        // object and the broker's saved numbers never come back on screen.
        [EField.RentmatchScore]: {
          default: getValue(
            EField.RentmatchScore,
            initialValues,
            defaultValues,
          ),
        },
        [EField.VirtualTourUrl]: {
          type: ["string", "null"],
          default: getValue(
            EField.VirtualTourUrl,
            initialValues,
            defaultValues,
          ),
        },
      }),
    },
  });

export const uiSchema = ({
  hasRentMatchForBroker,
  listingUrlTemplate,
  websiteUrl,
  property,
  propertyId,
  onPhotosSaved,
  rentmatchIncomeRanges,
}: IExtraData): IUiSchema<EField> => ({
  // Both halves of the score live in one block, because neither of them means
  // anything on its own. Everything they are compared against is read out of
  // the form rather than off the property, so the block follows the price and
  // the bedroom count while they are still being typed.
  //
  // The two defaults are what this property scores on today: leaving the block
  // untouched, or emptying a field again, changes no score at all.
  [EField.RentmatchScore]: {
    "ui:field": ERentioFormField.RentmatchScore,
    minFactor: MIN_PERFECT_INCOME_FACTOR,
    getRent: (formData: any) =>
      (formData?.[EField.Price] || 0) + (formData?.[EField.Costs] || 0),
    getDefaultIncome: (formData: any) => {
      const rent =
        (formData?.[EField.Price] || 0) + (formData?.[EField.Costs] || 0);

      return rent > 0
        ? getPerfectIncomeForRent(rent, {
            incomeRatioRanges: rentmatchIncomeRanges,
          })
        : 0;
    },
    getDefaultHousehold: (formData: any) =>
      getMaxFullScoreHousehold(formData?.[EField.RoomCountBedroom]),
  },
  // A separate key rather than an edit of the old title: that one is live and
  // still correct for the form as it stands in production, which is a publish
  // form and nothing else. This one names what the form became.
  "rentio:navigationTitle": hasRentMatchForBroker
    ? getLocalizedText("property_info.rentmatch_form.settings_title")
    : ts.propertyInfoFormModalTitle(),
  ...(hasRentMatchForBroker && {
    "rentio:navigationSubTitle": getLocalizedText(
      "property_info.rentmatch_form.extra_tab_hint",
    ),
  }),
  ...(!hasRentMatchForBroker && {
    "ui:order": [EField.Price, EField.Costs],
  }),
  ...(hasRentMatchForBroker && {
    "ui:field": ERentioFormField.Grid,
    grid: {
      row: [
        // Four cards, in the order a broker fills them in: say whether this
        // goes on Rentmatch, describe what it costs and who fits, add the
        // pictures, then everything that is nice to have.
        //
        // The photos sit third on purpose. They used to close the form, after a
        // dozen optional fields, and a broker who stopped scrolling published a
        // property without a single picture.

        // The banner sits inside the first card, between the heading and the
        // choice it argues for. It is about publishing, not about the other
        // three cards, so it does not stand above them all.
        {
          col: {
            width: [1, 1],
            card: true,
            children: [
              {
                row: [
                  {
                    col: { width: [1, 1], children: [EField.PublishHeader] },
                  },
                  {
                    col: { width: [1, 1], children: [EField.RentMatchIntro] },
                  },
                  {
                    col: {
                      width: [1, 0.5],
                      children: [EField.IsRentMatchPublished],
                    },
                  },
                  {
                    col: { width: [1, 0.5], children: [] },
                  },
                ],
              },
            ],
          },
        },

        {
          col: {
            width: [1, 1],
            card: true,
            children: [
              {
                row: [
                  {
                    col: { width: [1, 1], children: [EField.BasicsHeader] },
                  },
                  {
                    col: { width: [1, 0.5], children: [EField.Price] },
                  },
                  {
                    col: { width: [1, 0.5], children: [EField.Costs] },
                  },
                  {
                    col: {
                      width: [1, 0.5],
                      children: [EField.RoomCountBedroom],
                    },
                  },
                  {
                    col: {
                      width: [1, 0.5],
                      children: [EField.AvailableAtDate],
                    },
                  },
                  // Below everything it reads from: the rent it is a multiple
                  // of, and the bedroom count the household number comes from.
                  {
                    col: { width: [1, 1], children: [EField.RentmatchScore] },
                  },
                  {
                    col: {
                      width: [1, 1],
                      children: [EField.ExternalListingUrl],
                    },
                  },
                ],
              },
            ],
          },
        },

        {
          col: {
            width: [1, 1],
            card: true,
            children: [
              {
                row: [
                  {
                    col: { width: [1, 1], children: [EField.MediaHeader] },
                  },
                  {
                    col: { width: [1, 1], children: [EField.Photos] },
                  },
                  {
                    col: { width: [1, 1], children: [EField.VirtualTourUrl] },
                  },
                ],
              },
            ],
          },
        },

        {
          col: {
            width: [1, 1],
            card: true,
            children: [
              {
                row: [
                  {
                    col: {
                      width: [1, 1],
                      children: [EField.ExtraDetailsHeader],
                    },
                  },
                  {
                    col: {
                      width: [1, 0.5],
                      children: [EField.RoomCountBathroom],
                    },
                  },
                  {
                    col: {
                      width: [1, 0.5],
                      children: [EField.RoomCountGarage],
                    },
                  },
                  {
                    col: {
                      width: [1, 0.5],
                      children: [EField.LivingSurfaceArea],
                    },
                  },
                  {
                    col: { width: [1, 0.5], children: [EField.EpcLabel] },
                  },
                  {
                    col: {
                      width: [1, 0.5],
                      children: [EField.BuildingTerraceSurfaceArea],
                    },
                  },
                  {
                    col: {
                      width: [1, 0.5],
                      children: [EField.BuildingGardenSurfaceArea],
                    },
                  },
                  {
                    col: { width: [1, 1], children: [EField.DescriptionLong] },
                  },
                ],
              },
            ],
          },
        },
      ],
    },
  }),
  [EField.RoomCountBedroom]: {
    "ui:field": ERentioFormField.Number,
    title: getLocalizedText("property.extra.interior.field.room_count_bedroom"),
  },
  [EField.AvailableAtDate]: {
    "ui:field": ERentioFormField.Date,
    title: getLocalizedText("property_info.form.available_at_date.label"),
  },
  [EField.Price]: {
    "ui:field": ERentioFormField.Number,
    formatSummary: (formData?: number) => formData && formatCurrency(formData),
    title: ts.propertyInfoFormPriceLabel(),
    placeholder: "0",
    suffixText: "EUR",
    isFloat: true,
  },
  [EField.Costs]: {
    "ui:field": ERentioFormField.Number,
    formatSummary: (formData?: number) => formData && formatCurrency(formData),
    title: ts.propertyInfoFormCostsLabel(),
    placeholder: "0",
    suffixText: "EUR",
    isFloat: true,
  },
  [EField.RentMatchIntro]: {
    "ui:field": ERentioFormField.HelpInfo,
    showCloseButton: false,
    subtitle: getLocalizedText("property_info.rentmatch_form.intro"),
    infoUrl: "https://rentmatch.be",
    infoLabel: getLocalizedText("system.more_info"),
  },
  // No title: the section header right above it says the same thing, and the
  // two buttons say it a third time.
  [EField.IsRentMatchPublished]: {
    "ui:field": ERentioFormField.SegmentedControl,
  },
  [EField.ExternalListingUrl]: {
    "ui:field": ERentioFormField.Text,
    title: getLocalizedText("property_info.form.external_listing_url.label"),
    placeholder: computeListingUrlPlaceholder(
      listingUrlTemplate,
      websiteUrl,
      property,
    ),
    optional: true,
  },
  // Extra property details (same data as /extra page, accessible here for convenience)
  [EField.RoomCountBathroom]: {
    "ui:field": ERentioFormField.Number,
    title: getLocalizedText(
      "property.extra.interior.field.room_count_bathroom",
    ),
    optional: true,
  },
  [EField.RoomCountGarage]: {
    "ui:field": ERentioFormField.Number,
    title: getLocalizedText("property.extra.interior.field.room_count_garage"),
    optional: true,
  },
  [EField.LivingSurfaceArea]: {
    "ui:field": ERentioFormField.Number,
    title: getLocalizedText(
      "property.extra.interior.field.living_surface_area",
    ),
    suffixText: "m²",
    isFloat: true,
    optional: true,
  },
  [EField.EpcLabel]: {
    "ui:field": ERentioFormField.DropdownSelect,
    title: getLocalizedText("editor.variable.property_epc_label"),
    formatSummary: (value: EPropertyEPCLabel) =>
      getLocalizedText(`epc.label.${value}`.toLowerCase()),
    optional: true,
  },
  [EField.BuildingTerraceSurfaceArea]: {
    "ui:field": ERentioFormField.Number,
    title: getLocalizedText(
      "property.extra.exterior.field.building_terrace_surface_area",
    ),
    suffixText: "m²",
    isFloat: true,
    optional: true,
  },
  [EField.BuildingGardenSurfaceArea]: {
    "ui:field": ERentioFormField.Number,
    title: getLocalizedText(
      "property.extra.exterior.field.building_garden_surface_area",
    ),
    suffixText: "m²",
    isFloat: true,
    optional: true,
  },
  // The amenity column holds up to 10000 characters, so the input caps there
  // rather than letting the API cut the text silently.
  [EField.DescriptionLong]: {
    "ui:field": ERentioFormField.Text,
    title: getLocalizedText("property.field.description_long"),
    multiline: true,
    maxLength: 10000,
    optional: true,
  },

  [EField.PublishHeader]: {
    "ui:field": ERentioFormField.SectionHeader,
    number: 1,
    title: getLocalizedText("property_info.rentmatch_form.section.publish"),
  },
  [EField.BasicsHeader]: {
    "ui:field": ERentioFormField.SectionHeader,
    number: 2,
    title: getLocalizedText("property_info.rentmatch_form.section.basics"),
  },
  [EField.MediaHeader]: {
    "ui:field": ERentioFormField.SectionHeader,
    number: 3,
    title: getLocalizedText("property_info.rentmatch_form.section.media"),
  },
  // Registered by the dashboard rather than by the shared field set, because
  // the gallery it draws belongs to this app.
  [EField.Photos]: {
    "ui:field": ERentioFormField.PropertyPhotos,
    propertyId,
    onSaved: onPhotosSaved,
  },
  [EField.ExtraDetailsHeader]: {
    "ui:field": ERentioFormField.SectionHeader,
    number: 4,
    title: getLocalizedText("property_info.rentmatch_form.section.extra"),
  },
  // The same field, the same amenity and the same rule as the property's Extra
  // tab, so whichever of the two a broker reaches for, Rentmatch reads one
  // answer. Plain text rather than a url field: what is pasted here comes out
  // of a viewer, and refusing everything that does not parse as one would keep
  // a working link from being stored.
  [EField.VirtualTourUrl]: {
    "ui:field": ERentioFormField.Text,
    title: getLocalizedText("property.extra.media.item.virtual_tour"),
    optional: true,
  },
});

// Returns true when a number field is considered "empty" or zero.
// Handles: null, undefined, "", 0 (number), "0" (string from placeholder),
// and any other value that coerces to 0 or NaN via parseFloat.
function isPriceInvalid(value: any): boolean {
  if (value === null || value === undefined || value === "") return true;
  const num = parseFloat(String(value));
  return isNaN(num) || num <= 0;
}

function isCostsEmpty(value: any): boolean {
  if (value === null || value === undefined || value === "") return true;
  const num = parseFloat(String(value));
  return isNaN(num);
}

export const getValidate =
  ({ hasRentMatchForBroker }: IExtraData) =>
  (formData: any, errors: FormValidation) => {
    // Both halves of the score are refused rather than corrected, and both
    // errors land on the one field the block is bound to.
    //
    // Below one and a half times the rent the hidden floor would drop under the
    // rent itself, which would award points to somebody who cannot pay the rent
    // at all, and that floor is never on screen to explain a silent correction.
    // A property where nobody may live cannot be scored at all, and half a
    // person is not a household.
    const rent = (formData[EField.Price] || 0) + (formData[EField.Costs] || 0);
    const score = formData[EField.RentmatchScore] || {};

    if (
      rent > 0 &&
      typeof score.perfectIncome === "number" &&
      score.perfectIncome < rent * MIN_PERFECT_INCOME_FACTOR
    ) {
      errors[EField.RentmatchScore]?.addError(
        getLocalizedText("property.rentmatch.perfect_income.error.minimum", {
          amount: formatCurrency(
            Math.ceil(rent * MIN_PERFECT_INCOME_FACTOR),
            false,
          ),
        }),
      );
    }

    if (
      typeof score.maxHousehold === "number" &&
      (score.maxHousehold < 1 || !Number.isInteger(score.maxHousehold))
    ) {
      errors[EField.RentmatchScore]?.addError(
        getLocalizedText("property.rentmatch.max_household.error.minimum"),
      );
    }

    // Nothing but a web address, refused here where the broker can still see
    // what they typed: the write path stores whatever it is handed, and this
    // value comes back on a page that turns it into a frame and a link.
    validationErrorsForMediaUrl(formData[EField.VirtualTourUrl]).forEach(
      error => {
        errors[EField.VirtualTourUrl]?.addError(error);
      },
    );

    // The publish choice is an explicit, required decision for RentMatch
    // brokers (user test finding: a subtle toggle was easy to overlook).
    if (hasRentMatchForBroker) {
      const publishChoice = formData[EField.IsRentMatchPublished];
      if (publishChoice == null) {
        errors[EField.IsRentMatchPublished]?.addError(
          getLocalizedText("system.form.error.required"),
        );
      }
    }

    // Required for every Rentmatch broker, whichever publish choice was made
    // (DEV-3180). Gating these on the choice meant a property could be saved as
    // "do not publish" without them and then published later from the CRM or
    // the MCP tool, neither of which checks anything.
    if (hasRentMatchForBroker) {
      if (isPriceInvalid(formData[EField.Price])) {
        errors[EField.Price]?.addError(
          getLocalizedText("system.form.error.required"),
        );
      }

      if (isCostsEmpty(formData[EField.Costs])) {
        errors[EField.Costs]?.addError(
          getLocalizedText("system.form.error.required"),
        );
      }

      if (
        formData[EField.RoomCountBedroom] === undefined ||
        formData[EField.RoomCountBedroom] === null ||
        formData[EField.RoomCountBedroom] === ""
      ) {
        errors[EField.RoomCountBedroom]?.addError(
          getLocalizedText("system.form.error.required"),
        );
      }

      if (!formData[EField.AvailableAtDate]) {
        errors[EField.AvailableAtDate]?.addError(
          getLocalizedText("system.form.error.required"),
        );
      }
    }

    return errors;
  };
